import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import { Colors } from 'modules/theme';

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
`;

const PillGroup = styled.div`
  display: inline-flex;
  align-items: center;
  background: ${Colors.white};
  border: 1px solid ${Colors.gray2};
  border-radius: 999px;
  padding: 3px;
  gap: 2px;
`;

const Pill = styled.button`
  appearance: none;
  border: none;
  background: ${({ active }) => (active ? Colors.black2 : 'transparent')};
  color: ${({ active }) => (active ? Colors.white : Colors.gray)};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.2px;
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.12s ease;
  white-space: nowrap;

  &:hover {
    color: ${({ active }) => (active ? Colors.white : Colors.black2)};
    background: ${({ active }) => (active ? Colors.black2 : Colors.lightGray)};
  }

  &:focus {
    outline: none;
  }
`;

const Popover = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: ${Colors.white};
  border: 1px solid ${Colors.gray2};
  border-radius: 10px;
  padding: 14px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 1000;
  min-width: 260px;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  label {
    font-size: 11px;
    font-weight: 600;
    color: ${Colors.gray};
    text-transform: uppercase;
    width: 42px;
  }

  .react-datepicker-wrapper,
  input {
    flex: 1;
  }

  input {
    padding: 6px 10px;
    border: 1px solid ${Colors.gray2};
    border-radius: 6px;
    font-size: 13px;
    width: 100%;
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: ${Colors.lightBlue};
    }
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
`;

const ActionButton = styled.button`
  appearance: none;
  border: none;
  background: ${({ primary }) => (primary ? Colors.black2 : 'transparent')};
  color: ${({ primary }) => (primary ? Colors.white : Colors.gray)};
  font-size: 12px;
  font-weight: 600;
  padding: 7px 14px;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: ${({ primary }) => (primary ? '#3a3636' : Colors.lightGray)};
    color: ${({ primary }) => (primary ? Colors.white : Colors.black2)};
  }

  &:focus {
    outline: none;
  }
`;

const ActiveRange = styled.div`
  font-size: 11px;
  color: ${Colors.gray};
  font-weight: 500;
`;

const startOfDay = d => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const endOfDay = d => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};

const toIso = d => (d ? new Date(d).toISOString() : null);

const presetRanges = () => {
  const now = new Date();
  const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
  const startOfYear = new Date(now.getFullYear(), 0, 1);

  return {
    all: { from: null, to: null },
    ytd: { from: toIso(startOfDay(startOfYear)), to: toIso(endOfDay(now)) },
    thisMonth: {
      from: toIso(startOfDay(startOfThisMonth)),
      to: toIso(endOfDay(now)),
    },
    lastMonth: {
      from: toIso(startOfDay(startOfLastMonth)),
      to: toIso(endOfDay(endOfLastMonth)),
    },
  };
};

const formatShort = iso => {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const rangesMatch = (a, b) =>
  (a.from || null) === (b.from || null) && (a.to || null) === (b.to || null);

export default class TimeRangePicker extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      popoverOpen: false,
      customFrom: props.range.from ? new Date(props.range.from) : null,
      customTo: props.range.to ? new Date(props.range.to) : null,
    };
    this.wrapperRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleDocClick);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleDocClick);
  }

  handleDocClick = e => {
    if (this.wrapperRef.current && !this.wrapperRef.current.contains(e.target)) {
      this.setState({ popoverOpen: false });
    }
  };

  applyPreset = key => {
    const presets = presetRanges();
    if (presets[key]) {
      this.setState({ popoverOpen: false });
      this.props.onChange(presets[key]);
    }
  };

  openCustom = () => {
    const { range } = this.props;
    this.setState({
      popoverOpen: true,
      customFrom: range.from ? new Date(range.from) : null,
      customTo: range.to ? new Date(range.to) : null,
    });
  };

  applyCustom = () => {
    const { customFrom, customTo } = this.state;
    this.setState({ popoverOpen: false });
    this.props.onChange({
      from: customFrom ? toIso(startOfDay(customFrom)) : null,
      to: customTo ? toIso(endOfDay(customTo)) : null,
    });
  };

  render() {
    const { range } = this.props;
    const { popoverOpen, customFrom, customTo } = this.state;
    const presets = presetRanges();

    const activeKey = (() => {
      if (rangesMatch(range, presets.all)) return 'all';
      if (rangesMatch(range, presets.ytd)) return 'ytd';
      if (rangesMatch(range, presets.thisMonth)) return 'thisMonth';
      if (rangesMatch(range, presets.lastMonth)) return 'lastMonth';
      return 'custom';
    })();

    const activeLabel = (() => {
      if (activeKey !== 'custom') return null;
      const from = formatShort(range.from);
      const to = formatShort(range.to);
      if (!from && !to) return null;
      if (from && to) return `${from} \u2013 ${to}`;
      if (from) return `Since ${from}`;
      return `Until ${to}`;
    })();

    return (
      <Wrapper ref={this.wrapperRef}>
        <PillGroup>
          <Pill active={activeKey === 'all'} onClick={() => this.applyPreset('all')} type="button">
            All time
          </Pill>
          <Pill active={activeKey === 'ytd'} onClick={() => this.applyPreset('ytd')} type="button">
            YTD
          </Pill>
          <Pill
            active={activeKey === 'thisMonth'}
            onClick={() => this.applyPreset('thisMonth')}
            type="button"
          >
            This month
          </Pill>
          <Pill
            active={activeKey === 'lastMonth'}
            onClick={() => this.applyPreset('lastMonth')}
            type="button"
          >
            Last month
          </Pill>
          <Pill active={activeKey === 'custom'} onClick={this.openCustom} type="button">
            Custom
          </Pill>
        </PillGroup>

        {activeLabel ? <ActiveRange>{activeLabel}</ActiveRange> : null}

        {popoverOpen && (
          <Popover>
            <FieldRow>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label>
                <span>From</span>
              </label>
              <DatePicker
                selected={customFrom}
                onChange={d => this.setState({ customFrom: d })}
                selectsStart
                startDate={customFrom}
                endDate={customTo}
                maxDate={customTo || new Date()}
                dateFormat="MM/dd/yyyy"
                placeholderText="Start date"
                isClearable
              />
            </FieldRow>
            <FieldRow>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label>
                <span>To</span>
              </label>
              <DatePicker
                selected={customTo}
                onChange={d => this.setState({ customTo: d })}
                selectsEnd
                startDate={customFrom}
                endDate={customTo}
                minDate={customFrom}
                maxDate={new Date()}
                dateFormat="MM/dd/yyyy"
                placeholderText="End date"
                isClearable
              />
            </FieldRow>
            <Actions>
              <ActionButton type="button" onClick={() => this.setState({ popoverOpen: false })}>
                Cancel
              </ActionButton>
              <ActionButton type="button" primary onClick={this.applyCustom}>
                Apply
              </ActionButton>
            </Actions>
          </Popover>
        )}
      </Wrapper>
    );
  }
}

TimeRangePicker.propTypes = {
  onChange: PropTypes.func.isRequired,
  range: PropTypes.shape({
    from: PropTypes.string,
    to: PropTypes.string,
  }),
};

TimeRangePicker.defaultProps = {
  range: { from: null, to: null },
};
